/**
 * AK-47 v8: Component-based per-night pricing snapshot
 * 
 * Архитектура: Opera/Mews style components per night.
 * USALI 12 совместимо (Lodging vs F&B разделены через components[].kind).
 * 
 * Схема контракта v=1. Любые breaking changes → инкремент v.
 * Forward-compat: при v != 1 snapshot игнорируется (graceful fallback в legacy режим).
 */

export type SnapshotMode = 'auto' | 'locked' | 'manual';

export type ComponentKind = 'lodging' | 'meal' | 'extra_bed' | 'crib' | 'child_discount' | 'discount' | 'surcharge';

export interface NightComponent {
  kind: ComponentKind;
  txn_code: string;
  amount: number;
  qty: number;
  // Optional details by kind
  meal_type?: 'breakfast' | 'lunch' | 'dinner';
  unit_price?: number;
  posting_rhythm?: 'per_person_per_night' | 'per_room_per_night' | 'per_stay';
  discount_percent?: number;
  applies_to?: string;
}

export interface NightEntry {
  date: string; // YYYY-MM-DD
  guests: {
    adults: number;
    children: number;
    children_ages: number[];
  };
  rate_plan: {
    id: string | null;
    name: string;
    ota_meal_codes: number[];
  };
  components: NightComponent[];
  night_total: number;
  rate_source: 'season' | 'exception' | 'derived' | 'manual';
}

export interface SnapshotTotals {
  lodging: number;
  meals_breakfast: number;
  meals_lunch: number;
  meals_dinner: number;
  extras: number;
  grand_total: number;
}

export interface NightlySnapshot {
  v: 1;
  mode: SnapshotMode;
  currency: string;
  created_at: string;
  created_by: string | null;
  last_modified_at: string;
  nights: NightEntry[];
  totals: SnapshotTotals;
}

export const SNAPSHOT_VERSION = 1 as const;

/**
 * Считает итог одной ночи как сумму компонентов.
 */
export function computeNightTotal(night: NightEntry): number {
  return night.components.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
}

/**
 * Считает агрегаты totals из массива ночей.
 * Используется и на UI и совпадает с тем, что прочитают generated columns в БД.
 */
export function computeTotals(nights: NightEntry[]): SnapshotTotals {
  const totals: SnapshotTotals = {
    lodging: 0,
    meals_breakfast: 0,
    meals_lunch: 0,
    meals_dinner: 0,
    extras: 0,
    grand_total: 0,
  };

  for (const night of nights) {
    for (const c of night.components) {
      const amt = Number(c.amount) || 0;
      switch (c.kind) {
        case 'lodging':
          totals.lodging += amt;
          break;
        case 'meal':
          if (c.meal_type === 'breakfast') totals.meals_breakfast += amt;
          else if (c.meal_type === 'lunch') totals.meals_lunch += amt;
          else if (c.meal_type === 'dinner') totals.meals_dinner += amt;
          else totals.extras += amt;
          break;
        case 'extra_bed':
        case 'crib':
        case 'surcharge':
          totals.extras += amt;
          break;
        case 'child_discount':
        case 'discount':
          // Скидки уже отрицательные — учитываем в lodging
          totals.lodging += amt;
          break;
      }
    }
    totals.grand_total += computeNightTotal(night);
  }

  return totals;
}

/**
 * Пересчитывает итоги и night_total для snapshot in-place (immutable).
 */
export function recomputeSnapshot(snapshot: NightlySnapshot): NightlySnapshot {
  const nights = snapshot.nights.map(n => ({
    ...n,
    night_total: computeNightTotal(n),
  }));
  return {
    ...snapshot,
    nights,
    totals: computeTotals(nights),
    last_modified_at: new Date().toISOString(),
  };
}

/**
 * Создаёт пустой snapshot для новой брони.
 */
export function createEmptySnapshot(params: {
  mode: SnapshotMode;
  currency?: string;
  createdBy?: string | null;
}): NightlySnapshot {
  const now = new Date().toISOString();
  return {
    v: SNAPSHOT_VERSION,
    mode: params.mode,
    currency: params.currency || 'RUB',
    created_at: now,
    created_by: params.createdBy || null,
    last_modified_at: now,
    nights: [],
    totals: {
      lodging: 0,
      meals_breakfast: 0,
      meals_lunch: 0,
      meals_dinner: 0,
      extras: 0,
      grand_total: 0,
    },
  };
}

/**
 * Список дат [checkIn, checkOut) включительно по check-in, исключительно по check-out.
 */
export function enumerateNights(checkIn: string, checkOut: string): string[] {
  const result: string[] = [];
  const start = new Date(checkIn + 'T00:00:00Z');
  const end = new Date(checkOut + 'T00:00:00Z');
  const cur = new Date(start);
  while (cur < end) {
    result.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return result;
}

/**
 * При продлении даты check-out: оставляет старые ночи неприкосновенными,
 * добавляет новые ночи через builder (обычно — get_rate + meal price).
 * 
 * Это ядро AK-47 v8 AUTO режима — «боль Розы» решается ровно здесь.
 */
export async function extendSnapshotForNewDates(
  snapshot: NightlySnapshot,
  newCheckIn: string,
  newCheckOut: string,
  buildNight: (date: string) => Promise<NightEntry>
): Promise<NightlySnapshot> {
  const requiredDates = enumerateNights(newCheckIn, newCheckOut);
  const existingByDate = new Map(snapshot.nights.map(n => [n.date, n]));

  const newNights: NightEntry[] = [];
  for (const date of requiredDates) {
    const existing = existingByDate.get(date);
    if (existing) {
      newNights.push(existing); // 🔒 неприкосновенно
    } else {
      newNights.push(await buildNight(date));
    }
  }

  return recomputeSnapshot({
    ...snapshot,
    nights: newNights,
  });
}

/**
 * Безопасный парсер snapshot из БД (jsonb).
 * Возвращает null если данные битые или версия неподдерживаемая.
 */
export function parseSnapshot(raw: unknown): NightlySnapshot | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (obj.v !== 1) {
    console.warn('[nightlySnapshot] Unsupported snapshot version:', obj.v);
    return null;
  }
  if (!Array.isArray(obj.nights) || !obj.totals) return null;
  return obj as unknown as NightlySnapshot;
}

/**
 * 🆕 AK-47 v8 Step 3 (legacy activation):
 * Строит snapshot из существующей брони — для активации покомпонентного режима
 * на legacy-бронях (snapshot=NULL).
 *
 * Источники данных:
 * 1) Цены за проживание — RPC `get_rates_matrix` (единый источник истины тарифов)
 * 2) Питание — таблица `hotel_meal_prices` если у тарифа есть `meal_type` (BB/HB/FB)
 *
 * Не модифицирует БД — возвращает готовый snapshot для save_booking_atomic.
 */
export async function buildSnapshotFromBooking(params: {
  supabase: any;
  hotelId: string;
  booking: {
    check_in_date: string;
    check_out_date: string;
    rate_plan_id?: string | null;
    room_id?: string | null;
    room_type?: string | null;
    adult_count?: number | null;
    children_count?: number | null;
    children_ages?: number[] | null;
    number_of_guests?: number | null;
  };
  createdBy?: string | null;
  currency?: string;
}): Promise<NightlySnapshot> {
  const { supabase, hotelId, booking } = params;
  const checkIn = booking.check_in_date;
  const checkOut = booking.check_out_date;
  if (!checkIn || !checkOut) throw new Error('Бронь без дат — невозможно построить snapshot');

  // Категория номера (для фильтрации матрицы)
  // ⚠️ В БД колонка называется room_category_id (НЕ category_id)
  let categoryId: string | null = null;
  if (booking.room_id) {
    const { data: room } = await supabase
      .from('rooms')
      .select('room_category_id')
      .eq('id', booking.room_id)
      .maybeSingle();
    categoryId = (room as any)?.room_category_id ?? null;
  }
  if (!categoryId && booking.room_type) {
    // room_type иногда содержит uuid категории — используем как fallback
    // (если это строка-имя — фильтр всё равно не сработает и цена будет 0,
    //  что лучше, чем взять цену чужой категории)
    const looksLikeUuid = /^[0-9a-f-]{36}$/i.test(booking.room_type);
    if (looksLikeUuid) categoryId = booking.room_type;
  }

  // Тариф + meal_type
  let ratePlan: { id: string | null; name: string; meal_type: string | null } = {
    id: booking.rate_plan_id ?? null,
    name: 'Базовый',
    meal_type: null,
  };
  if (booking.rate_plan_id) {
    const { data: rp } = await supabase
      .from('rate_plans')
      .select('id, name, meal_type')
      .eq('id', booking.rate_plan_id)
      .maybeSingle();
    if (rp) ratePlan = { id: rp.id, name: rp.name || 'Базовый', meal_type: rp.meal_type ?? null };
  }

  // Цены за проживание — get_rates_matrix
  const dates = enumerateNights(checkIn, checkOut);
  if (dates.length === 0) throw new Error('Период брони пуст');

  const lastNight = dates[dates.length - 1];
  const { data: matrix, error: matrixErr } = await supabase.rpc('get_rates_matrix', {
    p_hotel_id: hotelId,
    p_start_date: dates[0],
    p_end_date: lastNight,
    p_rate_plan_id: ratePlan.id,
  });
  if (matrixErr) throw new Error(`get_rates_matrix: ${matrixErr.message}`);

  const priceByDate = new Map<string, number>();
  for (const row of (matrix || []) as Array<{ category_id: string; rate_date: string; price: number }>) {
    if (!categoryId || row.category_id === categoryId) {
      priceByDate.set(row.rate_date, Number(row.price) || 0);
    }
  }

  // Питание (если у тарифа есть meal_type)
  type MealCfg = { meal_type: 'breakfast' | 'lunch' | 'dinner'; price_per_adult: number; price_per_child: number; child_free_age: number; txn_code: string };
  const mealConfigs: MealCfg[] = [];

  const mealTypeRaw = (ratePlan.meal_type || '').toUpperCase();
  const wantedMeals: Array<'breakfast' | 'lunch' | 'dinner'> = [];
  if (mealTypeRaw === 'BB' || mealTypeRaw === 'HB' || mealTypeRaw === 'FB') wantedMeals.push('breakfast');
  if (mealTypeRaw === 'HB' || mealTypeRaw === 'FB') wantedMeals.push('dinner');
  if (mealTypeRaw === 'FB') wantedMeals.push('lunch');

  if (wantedMeals.length > 0) {
    const { data: meals } = await supabase
      .from('hotel_meal_prices')
      .select('meal_type, price_per_adult, price_per_child, child_free_age, txn_code, active')
      .eq('hotel_id', hotelId)
      .eq('active', true);
    for (const m of (meals || []) as any[]) {
      const mt = String(m.meal_type || '').toLowerCase();
      if (wantedMeals.includes(mt as any)) {
        mealConfigs.push({
          meal_type: mt as any,
          price_per_adult: Number(m.price_per_adult) || 0,
          price_per_child: Number(m.price_per_child) || 0,
          child_free_age: Number(m.child_free_age) || 0,
          txn_code: m.txn_code || `MEAL_${mt.toUpperCase()}`,
        });
      }
    }
  }

  const adults = Number(booking.adult_count || booking.number_of_guests || 1);
  const childrenAges: number[] = Array.isArray(booking.children_ages)
    ? booking.children_ages.map((x) => Number(x) || 0)
    : [];
  const childrenCount = Number(booking.children_count || childrenAges.length || 0);

  // Сборка ночей
  const nights: NightEntry[] = dates.map((date) => {
    const lodgingPrice = priceByDate.get(date) ?? 0;
    const components: NightComponent[] = [];

    components.push({
      kind: 'lodging',
      txn_code: 'ROOM',
      amount: lodgingPrice,
      qty: 1,
      unit_price: lodgingPrice,
      posting_rhythm: 'per_room_per_night',
    });

    for (const meal of mealConfigs) {
      const payingChildren = childrenAges.length > 0
        ? childrenAges.filter((age) => age >= meal.child_free_age).length
        : childrenCount;
      const adultAmount = adults * meal.price_per_adult;
      const childAmount = payingChildren * meal.price_per_child;
      const total = adultAmount + childAmount;
      if (total > 0) {
        components.push({
          kind: 'meal',
          txn_code: meal.txn_code,
          meal_type: meal.meal_type,
          amount: total,
          qty: adults + payingChildren,
          unit_price: meal.price_per_adult,
          posting_rhythm: 'per_person_per_night',
        });
      }
    }

    const night_total = components.reduce((s, c) => s + (Number(c.amount) || 0), 0);
    return {
      date,
      guests: { adults, children: childrenCount, children_ages: childrenAges },
      rate_plan: { id: ratePlan.id, name: ratePlan.name, ota_meal_codes: [] },
      components,
      night_total,
      rate_source: 'season',
    };
  });

  const now = new Date().toISOString();
  return recomputeSnapshot({
    v: SNAPSHOT_VERSION,
    mode: 'auto',
    currency: params.currency || 'RUB',
    created_at: now,
    created_by: params.createdBy ?? null,
    last_modified_at: now,
    nights,
    totals: {
      lodging: 0, meals_breakfast: 0, meals_lunch: 0, meals_dinner: 0, extras: 0, grand_total: 0,
    },
  });
}
